import { Spinner } from '@/components/loading'
import { Button } from '@/components/ui/button'
import { GlobalErrorPage } from '@/components/ui/errors'
import { createFileRoute, Link } from '@tanstack/react-router'
import { lazy, Suspense, useMemo, useRef } from 'react'

const HomeStats = lazy(() => import('@/components/home/stats').then(mod => ({ default: mod.HomeStats })))
const HowToDownloadConsentVideo = lazy(() => import('@/components/home/downloadConsentVideo').then(mod => ({ default: mod.HowToDownloadConsentVideo })))
const PreProcedureEducationContainer = lazy(() => import('@/components/educationalMaterials/education').then(mod => ({ default: mod.PreProcedureEducationContainer })))
const SearchEducationalMaterial = lazy(() => import('@/components/home/searchEducationalMaterial').then(mod => ({ default: mod.SearchEducationalMaterial })))
const DownArrowOnPoster = lazy(() => import('@/components/home/wavySvg').then(mod => ({ default: mod.DownArrowOnPoster })))

export const Route = createFileRoute('/')({
  component: RouteComponent,
  errorComponent: GlobalErrorPage,
})

function Hero() {
  const containerRef = useRef<HTMLDivElement>(null)
  return (
    <div className="relative">
      <div className="px-2 lg:px-6 mt-6 lg:mt-8 mb-6 lg:mb-56 h-auto overflow-hidden">
        <div className="absolute bg-black/40 w-[calc(100%-1rem)] lg:w-[calc(100%-3rem)] h-full ml-2 lg:ml-6 top-0 left-0 rounded-3xl" />
        <div ref={containerRef} className="lg:min-h-[calc(100vh-230px)] z-50 w-full lg:max-h-[calc(100vh-160px)] bg-[url(/images/hero.png)] bg-cover bg-center lg:bg-left-bottom bg-no-repeat rounded-3xl">
          <div className="h-auto lg:h-[calc(100vh-230px)]">
            <div className="text-center lg:text-right text-white z-50 relative top-8 right-[50%] translate-x-[50%] lg:translate-x-0 lg:right-8">
              <span className="text-[10px] lg:text-sm font-normal mr-1">Powered by</span>
              <span className="text-base lg:text-xl tracking-wider font-bold">Tulu Health</span>
            </div>

            <div className="grid grid-cols-2 relative h-full gap-10 lg:gap-0 pb-8 lg:pb-0">
              <div className="col-span-2 lg:col-span-1 w-full lg:max-w-2xl p-8 my-auto">
                <h2 className="font-bold leading-[40px] lg:leading-[64px] text-white text-[32px] lg:text-[56px] text-center lg:text-left">
                  Download patient consent forms in multiple languages
                </h2>
                <p className="text-white mt-6 text-sm leading-[16px] lg:text-base">
                  We understand preparing for a surgery can be overwhelming at times.
                  To help you feel more confident,
                  we provide consent forms along with educational materials to keep you informed of every step.
                </p>
              </div>

              <Suspense fallback={<Spinner />}>
                <HomeStats />
              </Suspense>
            </div>
          </div>
        </div>
      </div>

      <Suspense fallback={<Spinner />}>
        <DownArrowOnPoster />
      </Suspense>
    </div>
  )
}

function DownloadConsentSop() {
  return (
    <div className="py-20 lg:pt-0 lg:pb-10 px-2 lg:mx-auto lg:container" style={{ contentVisibility: 'auto' }}>
      <h3 className="font-bold text-2xl lg:text-5xl mb-4 lg:mb-12 text-center">Download your consent form in a few simple steps</h3>

      <div className="rounded-3xl border shadow-sm p-4 lg:p-10 bg-white max-w-6xl mx-auto">
        <h4 className="font-bold text-xl lg:text-[2.5rem] mb-10">How to download Patient Consent</h4>

        <div className="flex gap-10 flex-col lg:flex-row">
          <Suspense fallback={<Spinner />}>
            <HowToDownloadConsentVideo />
          </Suspense>

          <div className="lg:max-w-xs flex flex-col gap-10">
            <p className="text-textGray">We care about your time. Watch this short video to learn how to navigate through the website quickly.</p>
            <Link to="/consent">
              <Button className="bg-bgMagenta w-full py-3 md:py-6">Proceed</Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

function PreProcedureEducation() {
  return (
    <div className="bg-white" style={{ contentVisibility: 'auto' }}>
      <div className="px-2 md:px-0 md:container py-4 md:py-20 md:mx-auto">
        <h3 className="font-bold text-2xl md:text-5xl mb-4 md:mb-12 text-center">Pre-Procedure Education</h3>

        <div className="grid grid-cols-2 gap-10 pb-10">
          <div className="col-span-2 lg:col-span-1 text-textGray px-3 text-sm md:text-base">
            Your journey is our commitment to care.
            We offer a variety of resources to help you understand your procedure,
            from educational videos and blogs to step-by-step guides and FAQs.
            These materials are designed to provide clarity, ease any concerns,
            and help you feel confident in the decisions you’re making
          </div>

          <Suspense fallback={<Spinner />}>
            <SearchEducationalMaterial className="col-span-2 lg:col-span-1" />
          </Suspense>
        </div>

        <Suspense fallback={<Spinner />}>
          <PreProcedureEducationContainer />
        </Suspense>
      </div>
    </div>
  )
}

function Collaborations() {
  const collaborations = useMemo(() => [
    { name: 'CAHO', link: 'https://caho.in', posterUrl: '/collaborators/caho.png' },
    { name: 'Patients for patient safety', link: '', posterUrl: '/collaborators/pfps.svg' },
    { name: 'Institute of Medicine and Law', link: '', posterUrl: '/collaborators/iml.svg' },
    { name: 'Tulu Health', link: '', posterUrl: '/collaborators/tulu-logo+tagline.svg' },
  ], [])

  return (
    <div className="bg-bgCyan" style={{ contentVisibility: 'auto' }}>
      <div className="mx-auto container py-4 md:py-10 px-2 md:px-0">
        <h3 className="font-bold text-2xl md:text-5xl mb-12 text-center">In Collaboration With</h3>

        <div className="my-4 md:my-8 flex items-center flex-col md:flex-row md:justify-between flex-wrap gap-6 md:gap-10">
          {collaborations.map(collab => (
            <Link
              to={collab.link}
              title={collab.name}
              key={collab.posterUrl}
              className="flex-1"
              {...(collab.link ? { href: collab.link, target: '_blank', rel: 'noreferrer noopener' } : {})}
            >
              <img loading="lazy" alt={collab.name} src={collab.posterUrl} className="max-w-64 md:max-w-none w-full" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  )
}

function RouteComponent() {
  return (
    <div className="bg-bgPage">
      <Hero />
      <DownloadConsentSop />
      <PreProcedureEducation />
      <Collaborations />
    </div>
  )
}
